import {Product} from '@shopify/hydrogen/storefront-api-types';
import {StylePreference} from '../backendApi';

// - Shared Types -
export interface GenericErrorType {
  readonly detail: string;
}

export interface ValidationErrorType {
  readonly errors: {[key: string]: string};
}

export interface StateOptionType {
  name: string;
  slug: string;
}

export interface BrandInfo {
  value: string;
  is_default: boolean;
}

export interface CommunicationChannel {
  name: string;
  slug: string;
  is_default_channel: boolean;
}

export interface MetadataType {
  readonly brands: Array<BrandInfo>;
  readonly event_types: Array<string>;
  readonly flavour_note_types: Array<string>;
  readonly communication_channels: Array<CommunicationChannel>;
  readonly member_whisky_budgets: Array<string>;
  readonly member_whisky_frequencies: Array<string>;
  readonly news_article_categories: Array<string>;
  readonly partner_stores: Array<string>;
  readonly policy_types: Array<string>;
  readonly product_categories: Array<string>;
  readonly product_event_types: Array<string>;
  readonly product_filters: Array<string>;
  readonly product_groups: Array<string>;
  readonly product_peat_levels: Array<string>;
  readonly product_published_states: Array<string>;
  readonly state_options: Array<StateOptionType>;
  readonly styles: Array<string>;
  readonly types: Array<string>;
}

export interface Paginated<T> {
  readonly count: number;
  readonly next: string | null;
  readonly previous: string | null;
  readonly results: ReadonlyArray<T>;
}

export type ValueOf<T> = T[keyof T];

export type MaybePaginated<T> = Paginated<T> | Paginated<T>['results'];

export type OptionType = {value: string; label: string};

export type InputOptionsType = {[key: string]: OptionType};

export type ValueOfOption<T extends InputOptionsType> = T[keyof T]['value'];

export const WhiskyBudget: InputOptionsType = Object.freeze({
  under100: {value: 'under-100', label: 'Under $100'},
  between100And200: {value: '100-200', label: '$100 - $200'},
  over200: {value: 'over-200', label: '$200+'},
});

export const WhiskyFrequency: InputOptionsType = Object.freeze({
  onceAWeek: {value: 'once-a-week', label: 'About once a week'},
  onceAMonth: {value: 'once-a-month', label: 'About once a month'},
  onceInThreeMonths: {
    value: 'once-in-three-months',
    label: 'About once every three months',
  },
});

export type InputOptionWithIconType = {
  value: string;
  label: string;
  iconName: string;
};

export type InputOptionsWithIconType = Record<string, InputOptionWithIconType>;

export const NewsArticleCategory: InputOptionsWithIconType = Object.freeze({
  announcement: {
    value: 'announcement',
    label: 'Announcements',
    iconName: 'icon-announcements',
  },
  brandFeature: {
    value: 'brand-feature',
    label: 'Brand Features',
    iconName: 'icon-brand-features',
  },
  event: {
    value: 'event',
    label: 'Whisky Events',
    iconName: 'icon-whisky-events',
  },
  interview: {
    value: 'interview',
    label: 'Interviews',
    iconName: 'icon-interviews',
  },
  news: {value: 'news', label: 'Whisky News', iconName: 'icon-whisky-news'},
  twlReview: {value: 'twl-review', label: 'Reviews', iconName: 'icon-reviews'},
});

export const CountryOfOrigin: InputOptionsWithIconType = Object.freeze({
  australia: {
    value: 'Australia',
    label: 'Australia',
    iconName: 'australia',
  },
  ireland: {
    value: 'Ireland',
    label: 'Ireland',
    iconName: 'ireland',
  },
  india: {
    value: 'India',
    label: 'India',
    iconName: 'india',
  },
  japan: {
    value: 'Japan',
    label: 'Japan',
    iconName: 'japan',
  },
  scotland: {
    value: 'Scotland',
    label: 'Scotland',
    iconName: 'scotland',
  },
  taiwan: {
    value: 'Taiwan',
    label: 'Taiwan',
    iconName: 'taiwan',
  },
  unitedStates: {
    value: 'United States',
    label: 'United States',
    iconName: 'united-states',
  },
  restOfTheWorld: {
    value: 'rest-of-the-world',
    label: 'Rest of the world',
    iconName: 'european-union',
  },
});

export const ProductCategory: InputOptionsType = Object.freeze({
  whisky: {value: 'whisky', label: 'Whisky'},
  rum: {value: 'rum', label: 'Rum'},
  gin: {value: 'gin', label: 'Gin'},
  vodka: {value: 'vodka', label: 'Vodka'},
  brandy: {value: 'brandy', label: 'Brandy'},
  barware: {value: 'barware', label: 'Barware'},
  glassware: {value: 'glassware', label: 'Glassware'},
  otherSpirits: {value: 'other-spirits', label: 'Other Spirits'},
  tequilaAndMezcal: {value: 'tequila-and-mezcal', label: 'Tequila & Mezcal'},
  liqueurAndFortifiedWine: {
    value: 'liqueur-and-fortified-wine',
    label: 'Liqeur & Fortified Wine',
  },
});

export const ProductEventType = Object.freeze({
  new: 'new',
  priceUp: 'price-up',
  priceDown: 'price-down',
  ranOutOfStock: 'ran-out-of-stock',
  restocked: 'restocked',
});

export const RewardsSubscriptionType = Object.freeze({
  monthly: 'monthly',
  annual: 'annual',
});

export const MemberCollectedProductStatusType: InputOptionsType = Object.freeze(
  {
    default: {
      value: '',
      label: '---',
    },
    closed: {
      value: 'closed',
      label: 'Closed',
    },
    open: {
      value: 'open',
      label: 'Open',
    },
    disposed: {
      value: 'disposed',
      label: 'Disposed',
    },
  },
);

export const MemberCollectedProductWhenToOpen: InputOptionsType = Object.freeze(
  {
    default: {
      value: null,
      label: '---',
    },
    asap: {
      value: 'asap',
      label: 'As soon as possible',
    },
    soon: {
      value: 'soon',
      label: 'Soon',
    },
    specialOccasion: {
      value: 'special-occasion',
      label: 'Special occasion',
    },
    someday: {
      value: 'someday',
      label: 'Someday',
    },
    never: {
      value: 'never',
      label: 'Never',
    },
    notSure: {
      value: 'not-sure',
      label: 'Not sure',
    },
  },
);

export const MemberCollectedProductDisposedReason: InputOptionsType =
  Object.freeze({
    default: {
      value: null,
      label: '---',
    },
    drunk: {
      value: 'drunk',
      label: 'Drunk',
    },
    sold: {
      value: 'sold',
      label: 'Sold',
    },
    gifted: {
      value: 'gifted',
      label: 'Gifted',
    },
    broken: {
      value: 'broken',
      label: 'Broken',
    },
    tainted: {
      value: 'tainted',
      label: 'Tainted',
    },
    drainPoured: {
      value: 'drain-poured',
      label: 'Drain Poured',
    },
  });

export const MemberProductSaleType: InputOptionsType = Object.freeze({
  default: {
    value: null,
    label: '---',
  },
  storeCredit: {
    value: 'store-credit',
    label: 'Store Credit',
  },
  cash: {
    value: 'cash',
    label: 'Cash',
  },
});

export const FlexibleDateType = Object.freeze({
  fullDate: 'full-date',
  monthYear: 'month-year',
  yearOnly: 'year-only',
});

export type AverageRatingType = {
  value: number;
  total: number;
};

// - Auth Types -
export interface AuthMemberType {
  readonly uuid: string;
  readonly profile_picture?: string;
  readonly forums_admin: boolean;
  readonly forums_available: boolean;
  readonly forums_terms_accepted: boolean;
  readonly is_rewards_member: boolean;
  readonly tags: Record<string, string>;
}

export interface AuthUserType {
  readonly email: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly date_joined: string;
  readonly member: AuthMemberType;
}

export interface UserWithTokenPairResponseType {
  readonly user: AuthUserType;
  readonly access: string;
  readonly refresh: string;
}

export type SocialAuthProviderType = 'apple' | 'facebook' | 'google';

export interface SocialAuthUserWithTokenPairResponseType
  extends UserWithTokenPairResponseType {
  readonly auth_provider: SocialAuthProviderType;
  readonly is_created: boolean;
}

export interface UserType {
  readonly uuid: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly date_joined: string;
}

// Ref: https://shopify.dev/docs/api/admin-graphql/latest/objects/GiftCard
export interface ShopifyGiftCardType {
  readonly id: number;
  readonly expiresOn: string | null;
  readonly balance: {
    readonly amount: string;
    readonly currencyCode: string;
  };
  readonly createdAt: string; // ISO 8601
  readonly updatedAt: string; // ISO 8601
  readonly deactivatedAt?: string | null; // ISO 8601
}

export interface MemberType {
  readonly user: UserType;
  readonly uuid: string;
  readonly full_name: string;
  readonly date_of_birth: string;
  readonly postcode: string;
  readonly phone_number: string;
  readonly whisky_budget: ValueOfOption<typeof WhiskyBudget>;
  readonly whisky_purchase_frequency: ValueOfOption<typeof WhiskyFrequency>;
  readonly preferred_communication_channels: string[];
  readonly can_receive_marketing_notifications: boolean;
  readonly whisky_style_preferences: StylePreference[];
  readonly profile_picture?: string;
  readonly stream_token?: string;
  readonly forums_admin: boolean;
  readonly forums_available: boolean;
  readonly forums_terms_accepted: boolean;
  readonly is_rewards_member?: boolean;
  readonly rewards_member_code?: string;
  readonly whisky_wallet_code?: string;
  readonly rewards_referral_code?: string;
  readonly rewards_subscription_type?: ValueOf<typeof RewardsSubscriptionType>;
  readonly rewards_customer_magic_link?: string;
  readonly is_rewards_subscription_renewal_pending: boolean;
  readonly activity_stats?: MyStatsType;
  readonly gift_card?: ShopifyGiftCardType;
}

export interface FlavourNote {
  readonly uuid: string;
  readonly name: string;
}

// - Membership Types -
export interface ProductReview {
  readonly uuid: string;
  readonly reviewed_at: string;
  readonly reviewed_by: string;
  readonly is_own: boolean;
  readonly reviewer_image: string;
  readonly title: string;
  readonly description: string;
  readonly rating: number;
  readonly aroma_enjoyment_score: number;
  readonly taste_enjoyment_score: number;
  readonly body_and_mouthfeel_score: number;
  readonly balance_and_complexity_score: number;
  readonly finish_length_score: number;
  readonly aroma_notes: ReadonlyArray<string>;
  readonly taste_notes: ReadonlyArray<string>;
  readonly finish_notes: ReadonlyArray<string>;
  readonly product: {
    readonly uuid: string;
    readonly title: string;
    readonly type: string;
    readonly shopify_resource_id: string;
    readonly country: string;
    readonly handle: string;
    readonly main_image: string;
  };
}

export interface UserReviewsMetadata {
  readonly brands: ReadonlyArray<string>;
  readonly types: ReadonlyArray<string>;
}

export interface UserReviewsQueryParams {
  own?: string;
  type?: string;
  brand?: string;
  order?: string;
  rating?: string;
  country?: string;
  category?: string;
  productUuid?: string;
}

export type MemberProductScoreType = 1 | 2 | 3 | 4 | 5;

export interface MemberProductType {
  readonly uuid: string;
  readonly member: {
    readonly uuid: string;
  };
  readonly product: {
    readonly uuid: string;
    readonly handle: string;
    readonly average_rating: AverageRatingType;
    readonly total_reviews: number;
    readonly shopify_resource_id: string;
    readonly title: string;
    readonly type: string;
    readonly country: string;
    readonly main_image: string;
  };
  readonly aroma_notes: ReadonlyArray<string>;
  readonly taste_notes: ReadonlyArray<string>;
  readonly finish_notes: ReadonlyArray<string>;
  readonly is_watched: boolean;
  readonly is_added_to_collection: boolean;
  readonly rating: MemberProductScoreType;
  readonly review_title: string;
  readonly review_description: string;
  readonly aroma_enjoyment_score: MemberProductScoreType;
  readonly balance_and_complexity_score: MemberProductScoreType;
  readonly body_and_mouthfeel_score: MemberProductScoreType;
  readonly finish_length_score: MemberProductScoreType;
  readonly taste_enjoyment_score: MemberProductScoreType;
  readonly reviewed_at: string;
  readonly matchedProduct?: Product;
}

export interface MemberCollectedProductMetadataType {
  readonly brands: ReadonlyArray<OptionType>;
  readonly distilleries: ReadonlyArray<OptionType>;
  readonly styles: ReadonlyArray<OptionType>;
  readonly types: ReadonlyArray<OptionType>;
}

export interface MemberCollectedProductNestedProductType {
  readonly uuid: string;
  readonly handle: string;
  readonly average_rating: AverageRatingType;
  readonly total_reviews: number;
  readonly shopify_resource_id: string;
  readonly title: string;
  readonly type: string;
  readonly country: string;
  readonly main_image: string;
}

export interface MemberCollectedProductNestedProductRequestType {
  readonly uuid: string;
  readonly title: string;
  readonly category: string;
  readonly status: string;
  readonly add_to_collection: boolean;
}

export interface MemberCollectedProductType {
  readonly uuid: string;
  readonly friendly_name: string;
  readonly volume_ml: number | string;
  readonly batch_or_cask_no: string;
  readonly bottle_number: number | string;
  readonly bottles_in_release: number | string;
  readonly distilled_date: string; // DD-MM-YYYY
  readonly distilled_date_type: ValueOf<typeof FlexibleDateType>;
  readonly bottled_date: string; // DD-MM-YYYY
  readonly bottled_date_type: ValueOf<typeof FlexibleDateType>;
  readonly acquisition_date: string; // DD-MM-YYYY
  readonly acquisition_date_type: ValueOf<typeof FlexibleDateType>;
  readonly acquisition_value: string;
  readonly acquired_from: string;
  readonly status: ValueOfOption<typeof MemberCollectedProductStatusType>;
  readonly closed_when_to_open: ValueOfOption<
    typeof MemberCollectedProductWhenToOpen
  >;
  readonly open_fill_level: number | string;
  readonly open_date: string; // DD-MM-YYYY
  readonly open_date_type: ValueOf<typeof FlexibleDateType>;
  readonly disposed_reason: ValueOfOption<
    typeof MemberCollectedProductDisposedReason
  >;
  readonly disposed_date: string; // DD-MM-YYYY
  readonly disposed_date_type: ValueOf<typeof FlexibleDateType>;
  readonly no_of_bottles: number | string;
  readonly est_per_bottle_value: string;
  readonly location_stored: string;
  readonly notes: string;
  readonly product: MemberCollectedProductNestedProductType;
  readonly product_request: MemberCollectedProductNestedProductRequestType;
  readonly created_at: string;
  readonly updated_at: string;
  // Attachments
  readonly image: string | null;
  readonly receipt: string | null;
  // Sale
  readonly is_available_for_sale: boolean;
  readonly sale_units_available: number | string;
  readonly sale_asking_price: string;
  readonly sale_reserve_price: string;
  readonly sale_type: ValueOfOption<typeof MemberProductSaleType>;
  readonly sale_terms_accepted_at: string;
}

export interface ProductRequestType {
  readonly uuid: string;
  readonly title: string;
  readonly category: string;
  readonly brand: string | null;
  readonly distillery: string | null;
  readonly country: string | null;
  readonly abv: string | null;
  readonly type: string | null;
  readonly age: string | null;
  readonly style: string | null;
  readonly peat_level: string | null;
  readonly series: string | null;
  readonly cask: string | null;
  readonly filter: string | null;
  readonly region: string | null;
  readonly image: string | null;
  readonly status: string | null;
  readonly rejection_reason: string | null;
  readonly product: string | null;
  readonly add_to_collection: boolean;
  readonly collected_product: string | null;
}
export interface ProductRequestMetadataType {
  readonly categories: ReadonlyArray<OptionType>;
  readonly brands: ReadonlyArray<OptionType>;
  readonly distilleries: ReadonlyArray<OptionType>;
  readonly countries: ReadonlyArray<OptionType>;
  readonly types: ReadonlyArray<OptionType>;
  readonly styles: ReadonlyArray<OptionType>;
  readonly peat_levels: ReadonlyArray<OptionType>;
  readonly series: ReadonlyArray<OptionType>;
  readonly filters: ReadonlyArray<OptionType>;
}
export interface MemberProductsBulkOperationResponseType {
  readonly created: number;
  readonly updated: number;
  readonly deleted: number;
}

// - Marketplace Types -
export interface WhiskyProductType {
  readonly uuid: string;
  readonly title: string;
  readonly slug: string;
  readonly shopify_resource_id: string;
}

// - Event Management Types -
export interface EventsIndexPageHeroBannerType {
  readonly uuid: string;
  readonly background_image_desktop_webp?: string;
  readonly background_image_desktop_jpeg: string;
  readonly background_image_mobile_webp?: string;
  readonly background_image_mobile_jpeg: string;
  readonly content_text: string;
  readonly main_button_text: string;
  readonly main_button_url: string;
  readonly secondary_button_text?: string;
  readonly secondary_button_url?: string;
  readonly title: string;
}

export interface EventsIndexPageSidebarPromotionTileType {
  readonly tile_position: 'top' | 'bottom';
  readonly name: string;
  readonly title: string;
  readonly content_text: string;
  readonly button_text: string;
  readonly button_url: string;
  readonly is_enabled: boolean;
}

export interface VirtualEventsIndexPageSidebarPromotionTileType {
  readonly tile_position: 'top' | 'bottom';
  readonly name: string;
  readonly title: string;
  readonly content_text: string;
  readonly button_text: string;
  readonly button_url: string;
  readonly is_enabled: boolean;
}

export interface EventTastingKitProductType {
  readonly title: string;
  readonly description: string;
  readonly shopify_resource_id: string;
  readonly uuid: string;
}

export interface EventsIndexPageHostYourEventSectionType {
  readonly introduction_text: string;
  readonly primary_button_text?: string;
  readonly primary_button_url?: string;
  readonly secondary_button_text?: string;
  readonly secondary_button_url?: string;
  readonly title: string;
  readonly subtitle: string;
  readonly tasting_kit_products: Array<EventTastingKitProductType>;
}

export interface EventDetailPageSidebarPromotionTileType {
  readonly button_text: string;
  readonly button_url: string;
  readonly content_text: string;
  readonly title: string;
}

export interface EventImageType {
  readonly uuid: string;
  readonly image_desktop_webp?: string;
  readonly image_desktop_jpeg?: string;
  readonly image_mobile_webp?: string;
  readonly image_mobile_jpeg?: string;
  readonly is_featured: boolean;
  readonly is_app_header_image: boolean;
}

export interface EventSessionType {
  readonly uuid: string;
  readonly title: string; // Ticket ShopifyVariant.title
  readonly starts_at: string;
  readonly ends_at?: string;
  readonly shopify_variant_id: string;
  readonly quantity_available: number; // Ticket ShopifyVariant.quantityAvailable (ShopifyVariant.inventoryQuantity in Admin GraphQL) [2022-07]
  readonly available_for_sale: boolean; // Ticket ShopifyVariant.availableForSale [2022-07]
  readonly price: string; // Ticket ShopifyVariant.priceV2.amount (ShopifyVariant.price in Admin GraphQL) [2022-07]
}

export type EventTypeType = 'show' | 'live' | 'virtual';

export interface EventsIndexType {
  readonly hero_banners: Array<EventsIndexPageHeroBannerType>;
  readonly host_your_event_section: EventsIndexPageHostYourEventSectionType;
  readonly sidebar_promotions: Array<EventsIndexPageSidebarPromotionTileType>;
  readonly years: Array<number>;
}

export interface VirtualEventsIndexType {
  readonly hero_banners: Array<EventsIndexPageHeroBannerType>;
  readonly sidebar_promotions: Array<VirtualEventsIndexPageSidebarPromotionTileType>;
  readonly years: Array<number>;
}

export interface EventDetailType {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly event_types: Array<EventTypeType>;
  readonly summary: string;
  readonly starts_at: string;
  readonly ends_at: string;

  readonly location?: string;
  readonly location_street_address?: string;
  readonly location_address_locality?: string;
  readonly location_postal_code?: string;
  readonly location_address_region?: string;
  readonly location_address_country?: string;

  readonly event_images: Array<EventImageType>;
  readonly content: string;
  readonly buy_ticket_button_text: string;
  readonly buy_ticket_button_url: string;
  readonly has_tickets: boolean;
  readonly external_url: string; // Relevant if has_tickets is true
  readonly is_multi_ticket_purchase_available: boolean; // Relevant if has_tickets is true
  readonly max_ticket_purchase_quantity?: number; // Relevant if has_tickets is true
  readonly has_limited_tickets: boolean; // Relevant if has_tickets is true
  readonly facebook_event_url?: string; // Relevant if has_tickets is true
  readonly sidebar_promotion?: EventDetailPageSidebarPromotionTileType;
  readonly event_sessions: Array<EventSessionType>;
  readonly shopify_product_id: string; // Relevant if has_tickets is true
  readonly embed_highlights: ReadonlyArray<HighlightType<HighlightPointType>>;
  readonly is_rewards_members_only: boolean;
}

// - News CMS Types -
export interface NewsPagesCommonSidebarPromotionTileType {
  readonly title: string;
  readonly content_text: string;
  readonly button_text: string;
  readonly button_url: string;
}

export interface NewsArticleListItemType {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly category: ValueOfOption<typeof NewsArticleCategory>;
  readonly label: string;
  readonly featured_image_desktop_webp?: string;
  readonly featured_image_desktop_jpeg?: string;
  readonly featured_image_mobile_webp?: string;
  readonly featured_image_mobile_jpeg?: string;
  readonly summary: string;
  readonly published_at: string;
  readonly author: string;
  readonly tags: Array<string>;
}

export interface NewsArticlesIndexType<
  ArticlesPage extends MaybePaginated<NewsArticleListItemType> = readonly NewsArticleListItemType[],
> {
  readonly articles: ReadonlyArray<NewsArticleListItemType>;
  readonly more_articles: ArticlesPage;
  readonly sidebar_promotion: NewsPagesCommonSidebarPromotionTileType;
  readonly published_years: ReadonlyArray<number>;
  readonly review_articles: ReadonlyArray<NewsArticleListItemType>;
}

export interface HighlightPointType {
  readonly description: string;
  readonly label: string;
}

export interface HighlightType<P extends HighlightPointType> {
  readonly title: string;
  readonly subtitle: string;
  readonly url?: string;
  readonly button_text: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
  readonly highlight_points: Array<P>;
}

export interface NewsArticleDetailType {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly category: ValueOfOption<typeof NewsArticleCategory>;
  readonly label: string;
  readonly featured_image_desktop_webp?: string;
  readonly featured_image_desktop_jpeg?: string;
  readonly featured_image_mobile_webp?: string;
  readonly featured_image_mobile_jpeg?: string;
  readonly summary: string;
  readonly published_at: string;
  readonly author: string;
  readonly tags: Array<string>;
  readonly content: string;
  readonly embed_highlights?: Array<HighlightType<HighlightPointType>>;
  readonly embed_marketplace_products?: Array<string>; // Array of Product.shopify_resource_id
  readonly sidebar_promotion?: NewsPagesCommonSidebarPromotionTileType;
  readonly published_years: ReadonlyArray<number>;
  readonly review_articles: ReadonlyArray<NewsArticleListItemType>;
  readonly associated_articles: ReadonlyArray<NewsArticleListItemType>;
  readonly is_rewards_members_only: boolean;
}

// - Venue Management Types -

export interface WhiskyBarSidebarPromotionTileType {
  readonly title: string;
  readonly button_text: string;
  readonly button_url: string;
  readonly link_text: string;
  readonly link_url: string;
}

export interface StateType {
  readonly uuid: string;
  readonly name: string;
  readonly slug: string;
  readonly image_webp?: string;
  readonly image_jpeg?: string;
  readonly state_bars_landing_page_heading: string;
  readonly state_distilleries_landing_page_heading: string;
  readonly bar_count: number;
  readonly distillery_count: number;
}

export interface RegionType {
  readonly name: string;
  readonly state: StateType;
}

export interface AreaType {
  readonly uuid: string;
  readonly name: string;
  readonly slug: string;
  readonly state: StateType;
  readonly region: RegionType;
  readonly bar_count: number;
  readonly distillery_count: number;
}

export interface AboutTheWhiskyBarListType {
  readonly introduction: string;
  readonly content: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
}

export interface WhiskyBarSpecialtyType {
  readonly name: string;
}

export interface WhiskyBarImageType {
  readonly uuid: string;
  readonly image_desktop_webp?: string;
  readonly image_desktop_jpeg: string;
  readonly image_mobile_webp?: string;
  readonly image_mobile_jpeg: string;
}

export interface WhiskyBarType {
  readonly uuid: string;
  readonly slug: string;
  readonly name: string;
  readonly tagline: string | null;
  readonly number_of_whiskies: number;
  readonly address: string;
  readonly suburb: string | null;
  readonly map_address_url: string | null;
  readonly website_url: string | null;
  readonly summary: string;
  readonly content: string;
  readonly is_partner: boolean;
  readonly specialities: ReadonlyArray<WhiskyBarSpecialtyType>;
  readonly state: StateType;
  readonly area: AreaType | null;
  readonly sidebar_promotion: WhiskyBarSidebarPromotionTileType | null;
  readonly tags: Array<string>;
  readonly images: Array<WhiskyBarImageType>;
  readonly highlights: Array<HighlightType<HighlightPointType>>;
}

export interface WhiskyBarsIndexType {
  readonly states: ReadonlyArray<StateType>;
  readonly sidebar_promotion: WhiskyBarSidebarPromotionTileType | null;
  readonly featured_bar: WhiskyBarType;
  readonly recently_featured_bars: ReadonlyArray<WhiskyBarType>;
  readonly about: AboutTheWhiskyBarListType;
}

export interface DistilleriesIndexType {
  readonly featured_distillery: DistilleryType;
  readonly whisky_bars: ReadonlyArray<WhiskyBarType>;
  readonly distilleries_count: number;
  readonly states: ReadonlyArray<StateType>;
  readonly spirit_types: ReadonlyArray<SpiritTypeType>;
}

export interface DistilleryType {
  readonly uuid: string;
  readonly name: string;
  readonly established_year: number;
  readonly suburb: string | null;
  readonly map_address_url: string | null;
  readonly website_url: string;
  readonly is_partner: boolean;
  readonly spirit_types: ReadonlyArray<string>;
  readonly image_webp?: string;
  readonly image_jpeg: string;
  readonly state: StateType;
  readonly area: AreaType | null;
}

export interface SpiritTypeType {
  readonly uuid: string;
  readonly name: string;
}

export interface WhiskyMapFeaturedTileType {
  readonly uuid: string;
  readonly title: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
  readonly link: string;
}

// - Site Navigation Types -
export interface SiteTopAlertBarType {
  readonly is_enabled: boolean;
  readonly is_homepage_only: boolean;
  readonly content: string;
}

export type LinkTargetType = '_self' | '_blank';

export interface NavigationLinkType {
  readonly link_type: 'text' | 'country';
  readonly country_icon: string;
  readonly link_label: string;
  readonly link_tag: string;
  readonly link: string;
  readonly link_target: LinkTargetType;
}

export interface LinkListNavColumnType {
  readonly uuid: string;
  readonly column_type: 'link_list';
  readonly column_heading: string;
  readonly column_position: 'column-1' | 'column-2' | 'column-3' | 'column-4';
  readonly navigation_links: ReadonlyArray<NavigationLinkType>;
}

export interface FeatureLinkNavColumnType {
  readonly uuid: string;
  readonly column_type: 'feature_link';
  readonly link: string;
  readonly link_text: string;
  readonly link_target: LinkTargetType;
  readonly background_color: string;
  readonly font_color: string;
  readonly title: string;
  readonly content_text: string;
  readonly feature_image: string;
}

export const MainNavigationCategoryName: Record<string, string> = Object.freeze(
  {
    whisky: 'whisky',
    eventsAndTastings: 'events-and-tastings',
    distilleriesAndBars: 'distilleries-and-bars',
    rewards: 'rewards',
    community: 'community',
    newsAndReviews: 'news-and-reviews',
    samplesAndGifts: 'samples-and-gifts',
    whiskyGuides: 'whisky-guides',
  },
);

export interface MainNavigationCategoryType {
  readonly category_name: ValueOf<typeof MainNavigationCategoryName>;
  // - featured link 1
  readonly featured_link1_image?: string;
  readonly featured_link1_text?: string;
  readonly featured_link1_url?: string;
  readonly featured_link1_target?: LinkTargetType;
  // - featured link 2
  readonly featured_link2_image?: string;
  readonly featured_link2_text?: string;
  readonly featured_link2_url?: string;
  readonly featured_link2_target?: LinkTargetType;
  // - extra link
  readonly extra_link_text?: string;
  readonly extra_link_url?: string;
  readonly extra_link_target?: LinkTargetType;
  readonly columns: ReadonlyArray<
    LinkListNavColumnType | FeatureLinkNavColumnType
  >;
}

export interface NavigationType {
  readonly site_top_alert: SiteTopAlertBarType;
  readonly main_navigation: ReadonlyArray<MainNavigationCategoryType>;
  readonly footer_navigation: ReadonlyArray<FooterNavColumnType>;
}

interface FooterNavigationLinkType {
  readonly link_text: string;
  readonly link: string;
  readonly link_target: LinkTargetType;
}

export interface FooterNavColumnType {
  readonly column_name: string;
  readonly footer_navigation_links: ReadonlyArray<FooterNavigationLinkType>;
}

export interface GeneralPageType {
  readonly uuid: string;
  readonly published_at: string;
  readonly title: string;
  readonly subheading: string;
  readonly content: string;
  readonly slug: string;
  readonly heading_image?: string;
  readonly meta_title?: string;
  readonly meta_description?: string;
  readonly embed_highlights: ReadonlyArray<HighlightType<HighlightPointType>>;
}

export interface LandingPageHeroBannerType {
  readonly uuid: string;
  readonly image_desktop_webp?: string;
  readonly image_desktop_jpeg: string;
  readonly image_mobile_webp?: string;
  readonly image_mobile_jpeg: string;
}

export interface LandingPageIconType {
  readonly uuid: string;
  readonly icon: string;
  readonly name: string;
  readonly text: string;
}

export interface LandingPageBenefitType {
  readonly uuid: string;
  readonly icon: string;
  readonly text: string;
}

export interface LandingPageFeatureType {
  readonly uuid: string;
  readonly name: string;
  readonly text: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
}

export interface LandingPageTestimonialType {
  readonly uuid: string;
  readonly name: string;
  readonly text: string;
  readonly stars: number;
  readonly image_webp?: string;
  readonly image_jpeg: string;
}

export interface LandingPageFaqType {
  readonly uuid: string;
  readonly question: string;
  readonly answer: string;
}

export interface LandingPageType {
  readonly uuid: string;
  readonly published_at: string;
  readonly title: string;
  readonly meta_image: string;
  readonly meta_title: string;
  readonly meta_description: string;
  readonly meta_extra: string;
  readonly slug: string;
  readonly hero_banners: ReadonlyArray<LandingPageHeroBannerType>;
  readonly show_cta_section: boolean;
  readonly cta_title: string;
  readonly cta_subtitle: string;
  readonly cta_bottom_text: string;
  readonly cta_button_text: string;
  readonly cta_button_url: string;
  readonly cta_append_discount: boolean;
  readonly show_intro_section: boolean;
  readonly intro_title: string;
  readonly intro_content: string;
  readonly intro_button_text: string;
  readonly intro_button_url: string;
  readonly intro_append_discount: boolean;
  readonly intro_image_webp: string;
  readonly intro_image_jpeg: string;
  readonly show_icons_section: boolean;
  readonly icons: ReadonlyArray<LandingPageIconType>;
  readonly show_benefits_section: boolean;
  readonly benefits_title: string;
  readonly benefits_content: string;
  readonly benefits_image_webp: string;
  readonly benefits_image_jpeg: string;
  readonly benefits: ReadonlyArray<LandingPageBenefitType>;
  readonly show_features_section: boolean;
  readonly features_title: string;
  readonly features: ReadonlyArray<LandingPageFeatureType>;
  readonly show_products_section: boolean;
  readonly products_title: string;
  readonly products_content: string;
  readonly products_button_text: string;
  readonly products_button_url: string;
  readonly products_append_discount: boolean;
  readonly products: ReadonlyArray<string>;
  readonly show_bcta_section: boolean;
  readonly bcta_title: string;
  readonly bcta_subtitle: string;
  readonly bcta_bottom_text: string;
  readonly bcta_button_text: string;
  readonly bcta_button_url: string;
  readonly bcta_append_discount: boolean;
  readonly show_testimonials_section: boolean;
  readonly testimonials_title: string;
  readonly testimonials: ReadonlyArray<LandingPageTestimonialType>;
  readonly show_faqs_section: boolean;
  readonly faqs_title: string;
  readonly faqs: ReadonlyArray<LandingPageFaqType>;
}

export interface RewardsPageType {
  title: string;
  description: string;
  meta_image: string;
  html: string;
  faqs: ReadonlyArray<LandingPageFaqType>;
}

export interface RedirectType {
  path: string;
}

// - Homepage Types -
export interface HomepageHeroBannerType {
  readonly uuid: string;
  readonly name: string;
  readonly banner_type: 'image' | 'html';
  readonly slider_behaviour: 'auto' | 'manual';
  readonly image_mask: '' | 'light' | 'moderate' | 'heavy';
  readonly image_desktop_webp?: string;
  readonly image_desktop_jpeg: string;
  readonly image_mobile_webp?: string;
  readonly image_mobile_jpeg: string;
  readonly slogan_heading?: string;
  readonly title: string;
  readonly link: string;
  readonly call_to_action_text: string;
  readonly font_color: string;
}

export interface HomepageJoinCommunitySectionType {
  readonly uuid: string;
  readonly title: string;
  readonly slogan_content: string;
  readonly image_desktop_webp?: string;
  readonly image_desktop_jpeg: string;
  readonly image_badge_text?: string;
  readonly feature_box1_title: string;
  readonly feature_box1_text: string;
  readonly feature_box2_title: string;
  readonly feature_box2_text: string;
  readonly feature_box3_title: string;
  readonly feature_box3_text: string;
  readonly feature_box4_title: string;
  readonly feature_box4_text: string;
  readonly button_text: string;
  readonly button_url: string;
}

export interface HomepageFeaturedTileType {
  readonly uuid: string;
  readonly order: number;
  readonly subtitle: string;
  readonly main_title: string;
  readonly button_text: string;
  readonly tile_url: string;
  readonly image_webp?: string;
  readonly image_png: string;
  readonly background_color: string;
  readonly font_color: string;
}

export interface NewsAndEventItemType {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly item_type: string;
  readonly published_at: string;
  readonly featured_image_desktop_jpeg: string;
  readonly featured_image_desktop_webp: string | null;
}

export interface NewsReview {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly published_at: string;
}

export interface HomepageContentType {
  readonly hero_banners: ReadonlyArray<HomepageHeroBannerType>;
  readonly join_community: HomepageJoinCommunitySectionType;
  readonly featured_tiles: ReadonlyArray<HomepageFeaturedTileType>;
  readonly product_reviews: ReadonlyArray<ProductReview>;
  readonly news_reviews: ReadonlyArray<NewsReview>;
  readonly news_and_events: {
    readonly first_item: NewsAndEventItemType;
    readonly second_item: NewsAndEventItemType;
    readonly big_item: NewsAndEventItemType;
  };
}

// - Community Types -
export interface AboutTwlFeaturedTileType {
  readonly image_jpeg: string;
  readonly image_webp?: string;
  readonly position: 'left' | 'middle' | 'right';
  readonly title: string;
  readonly url: string;
}

export interface AboutTwlType {
  readonly hero_image_jpeg?: string;
  readonly hero_image_webp?: string;
  readonly introduction_heading: string;
  readonly introduction_text: string;
  readonly our_reason_for_being_content: string;
  readonly our_reason_for_being_image_jpeg?: string;
  readonly our_reason_for_being_image_webp?: string;
  readonly our_reason_for_being_title: string;
  readonly title: string;
  readonly who_are_we_content: string;
  readonly who_are_we_image_jpeg?: string;
  readonly who_are_we_image_webp?: string;
  readonly who_are_we_title: string;
  readonly featured_tiles: ReadonlyArray<AboutTwlFeaturedTileType>;
}

export interface WhiskyLoversPageType {
  readonly title: string;
  readonly hero_image_jpeg?: string;
  readonly hero_image_webp?: string;
  readonly content_heading: string;
  readonly content_text: string;
  readonly join_link: string;
  readonly sponsor_link: string;
  readonly youtube_video_heading: string;
  readonly youtube_video_link?: string;
}

export interface GuidePageType {
  readonly uuid: string;
  readonly published_at: string;
  readonly title: string;
  readonly subheading: string;
  readonly content: string;
  readonly slug: string;
  readonly heading_image?: string;
  readonly meta_title?: string;
  readonly meta_description?: string;
  readonly embed_highlights: ReadonlyArray<HighlightType<HighlightPointType>>;
}

export interface WhiskyHubFeaturedTileType {
  readonly uuid: string;
  readonly title: string;
  readonly slug: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
  readonly short_summary: string;
  readonly button_text: string;
}

export interface WhiskyHubWhiskyTypeTileType {
  readonly uuid: string;
  readonly title: string;
  readonly slug: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
  readonly short_summary: string;
  readonly country_of_origin: string;
}

export interface WhiskyHubType {
  readonly title: string;
  readonly subtitle: string;
  readonly featured_tiles: ReadonlyArray<WhiskyHubFeaturedTileType>;
  readonly whisky_type_tiles: ReadonlyArray<WhiskyHubWhiskyTypeTileType>;
}

export interface FeaturedBrandHighlightPointType extends HighlightPointType {
  readonly uuid: string;
}

export interface FeaturedBrandHighlightType
  extends HighlightType<FeaturedBrandHighlightPointType> {
  readonly uuid: string;
}

export interface FeaturedBrandHeroBannerType {
  readonly uuid: string;
  readonly desktop_image_webp?: string;
  readonly desktop_image_jpeg: string;
  readonly mobile_image_webp?: string;
  readonly mobile_image_jpeg: string;
}

export interface FeaturedBrandType {
  readonly uuid: string;
  readonly name: string;
  readonly slug: string;
  readonly tagline: string;
  readonly country_icon: string;
  readonly thumbnail_image_webp?: string;
  readonly thumbnail_image_jpeg: string;
  readonly summary: string;
  readonly content: string;
  readonly marketplace_brand?: string;
  readonly embed_highlights?: Array<FeaturedBrandHighlightType>;
  readonly images: Array<FeaturedBrandHeroBannerType>;
  readonly is_rewards_members_only: boolean;
}

export interface PartnersPageTileType {
  readonly uuid: string;
  readonly name: string;
  readonly summary: string;
  readonly url?: string;
  readonly thumbnail_image_webp?: string;
  readonly thumbnail_image_jpeg: string;
  readonly button_text: string;
  readonly button_url: string;
}

export interface PartnersPageType {
  readonly title: string;
  readonly subtitle: string;
  readonly tiles: ReadonlyArray<PartnersPageTileType>;
}

export interface RetailPartnerType {
  readonly uuid: string;
  readonly name: string;
  readonly tagline?: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
  readonly is_online: boolean;
  readonly website_url?: string;
  readonly state?: string;
  readonly suburb?: string;
}

interface PartnersIndexPageType {
  readonly title: string;
  readonly subtitle: string;
  readonly promotion_widget_is_enabled: boolean;
  readonly promotion_widget_title: string;
  readonly promotion_widget_text: string;
  readonly promotion_widget_button_text: string;
  readonly promotion_widget_button_url: string;
}

export interface RetailPartnersPageType extends PartnersIndexPageType {
  readonly partners: ReadonlyArray<RetailPartnerType>;
}

export interface IndustryPartnerType {
  readonly uuid: string;
  readonly name: string;
  readonly tagline?: string;
  readonly image_webp?: string;
  readonly image_jpeg: string;
  readonly is_online: boolean;
  readonly website_url?: string;
  readonly state?: string;
  readonly suburb?: string;
}

export interface IndustryPartnersPageType extends PartnersIndexPageType {
  readonly partners: ReadonlyArray<IndustryPartnerType>;
}

// - Misc Types -
export const PolicyType = Object.freeze({
  privacy: 'privacy',
  terms: 'terms',
  shipping: 'shipping',
  returns: 'returns',
});

export interface PolicyContentType {
  readonly type: ValueOf<typeof PolicyType>;
  readonly title: string;
  readonly content: string;
  readonly slug: string;
  readonly slug_path?: string;
  readonly hero_image_webp?: string;
  readonly hero_image_jpeg: string;
}

export interface ContactInfoType {
  readonly detail: string;
}

export interface PartnerWithUsPageType {
  readonly title: string;
  readonly subtitle: string;
  readonly sidebar_content: string;
  readonly feature_section_title: string;
  readonly feature_section_text: string;
  readonly partners: ReadonlyArray<RetailPartnerType>;
}

export interface ProductAggregatedNotesType {
  readonly aroma_notes: ReadonlyArray<string>;
  readonly taste_notes: ReadonlyArray<string>;
  readonly finish_notes: ReadonlyArray<string>;
}

export interface MemberDashboardNewsArticleType {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly label: string;
  readonly category: ValueOfOption<typeof NewsArticleCategory>;
  readonly published_at: string;
  readonly featured_image_desktop_webp?: string;
  readonly featured_image_desktop_jpeg?: string;
  readonly featured_image_mobile_webp?: string;
  readonly featured_image_mobile_jpeg?: string;
  readonly tags: ReadonlyArray<string>;
}

export interface MemberDashboardFeaturedBrandType {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly label: string;
  readonly category: ValueOfOption<typeof NewsArticleCategory>;
  readonly published_at: string;
  readonly featured_image_desktop_webp?: string;
  readonly featured_image_desktop_jpeg?: string;
  readonly featured_image_mobile_webp?: string;
  readonly featured_image_mobile_jpeg?: string;
  readonly tags: ReadonlyArray<string>;
}

export interface MemberDashboardEventType {
  readonly uuid: string;
  readonly slug: string;
  readonly title: string;
  readonly event_types: ReadonlyArray<EventTypeType>;
  readonly starts_at: string;
  readonly featured_image_desktop_webp?: string;
  readonly featured_image_desktop_jpeg: string;
  readonly featured_image_mobile_webp?: string;
  readonly featured_image_mobile_jpeg?: string;
}

export interface MyStatsType {
  readonly total_collected: number;
  readonly total_collected_units: number;
  readonly total_watched: number;
  readonly total_rated: number;
  readonly total_reviewed: number;
}

export interface DashboardContentType {
  readonly giveaway_explanation: string;
  readonly free_whisky_explanation: string;
  readonly points_explanation: string;
}

export interface MemberGiveawayType {
  readonly prize_name: string;
  readonly prize_value: string;
  readonly end_datetime: string;
  readonly image_jpeg: string;
  readonly image_webp?: string;
}

export interface MemberRewardsDashboardType {
  readonly points_balance: number;
  readonly wallet_balance: string;
  readonly lifetime_points_earned: number;
  readonly top_ups_earned: number;
  readonly rewards_savings: number;
  readonly whiskies_rated: number;
  readonly added_to_collection: number;
  readonly added_to_watch_list: number;
  readonly virtual_tastings: number;
  readonly shows_and_events: number;
  readonly monthly_giveaway_entries: number;
  readonly bottles_purchased: number;
  readonly free_whisky_target: number;
  readonly free_whisky_desc: string;
}

export interface MyDashboardType {
  readonly featured_news_articles: ReadonlyArray<MemberDashboardNewsArticleType>;
  readonly featured_brands: ReadonlyArray<MemberDashboardFeaturedBrandType>;
  readonly featured_events: ReadonlyArray<MemberDashboardEventType>;
  readonly activity_stats: MyStatsType;
  readonly recent_reviews: ReadonlyArray<ProductReview>;
  readonly content: DashboardContentType;
  readonly rewards_info?: MemberRewardsDashboardType;
  readonly rewards_giveaway: MemberGiveawayType;
  readonly rewards_post: string;
}

export interface ProductHandleLookupType {
  readonly handle: string;
}

export interface MemberEventType {
  readonly id: number;
  readonly event_id: string;
  readonly event_slug?: string;
  readonly event_name?: string;
  readonly event_type?: string;
  readonly location?: string;
  readonly event_date_time?: string;
  readonly tickets: number;
}

export interface MemberOrderType {
  readonly order_number: number;
  readonly order_date: string;
  readonly order_status: string;
  readonly quantity: number;
  readonly amount: number;
  readonly public_discount: number;
  readonly rewards_discount: number;
  readonly credit_used: number;
  readonly total_paid: number;
  readonly points_earned: number;
  readonly line_items: ReadonlyArray<MemberOrderLineItemType>;
}

export interface MemberOrderLineItemType {
  readonly id: number;
  readonly shipping_status: string;
  readonly tracking_number: string;
  readonly tracking_url: string;
  readonly product: string;
  readonly path: string | null;
  readonly quantity: number;
  readonly amount: number;
  readonly public_discount: number;
  readonly rewards_discount: number;
  readonly credit_used: number;
  readonly total_paid: number;
  readonly points_earned: number;
  readonly double_points: boolean;
}

export interface ShopPromoTileType {
  readonly uuid: string;
  readonly subtitle: string;
  readonly main_title: string;
  readonly button_text: string;
  readonly tile_url: string;
  readonly image_webp: string | null;
  readonly image_png: string;
  readonly background_color: string;
  readonly font_color: string;
  readonly position: number;
}
